import type { NavigationResponse } from '@portal/module-core-system/contracts';
import { apiFetch } from '@portal/web-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type NavNode = NavigationResponse['items'][number];

/** Navigation tree for the current user, built by the API from module manifests and permissions. */
export function useNavigation() {
  return useQuery({
    queryKey: ['system', 'navigation'],
    queryFn: () => apiFetch<NavigationResponse>('system/navigation'),
    staleTime: 5 * 60_000,
  });
}

/** Every navigable item (has a path), depth-first, with its ancestor chain. */
export function flattenNavigation(
  items: NavNode[],
  parents: NavNode[] = [],
): Array<{ node: NavNode; trail: NavNode[] }> {
  return items.flatMap((node) => [
    ...(node.path ? [{ node, trail: [...parents, node] }] : []),
    ...flattenNavigation(node.children ?? [], [...parents, node]),
  ]);
}

/** Breadcrumb for a pathname: the trail of the longest navigation path that prefixes it. */
export function findTrail(items: NavNode[], pathname: string): NavNode[] {
  let best: { trail: NavNode[]; length: number } | undefined;
  for (const { node, trail } of flattenNavigation(items)) {
    const path = node.path!;
    const matches =
      path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
    if (matches && (!best || path.length > best.length)) best = { trail, length: path.length };
  }
  return best?.trail ?? [];
}

const favoritesKey = ['system', 'favorites'] as const;

/** Pinned pages of the current user (stored on the server, so they follow the user across devices). */
export function useFavorites() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: favoritesKey,
    queryFn: () => apiFetch<{ navIds: string[] }>('system/favorites'),
    staleTime: 5 * 60_000,
  });
  const save = useMutation({
    mutationFn: (navIds: string[]) =>
      apiFetch<{ navIds: string[] }>('system/favorites', {
        method: 'PUT',
        body: JSON.stringify({ navIds }),
      }),
    onMutate: (navIds) => queryClient.setQueryData(favoritesKey, { navIds }),
    onSuccess: (data) => queryClient.setQueryData(favoritesKey, data),
  });
  const navIds = query.data?.navIds ?? [];
  return {
    navIds,
    isFavorite: (id: string) => navIds.includes(id),
    toggle: (id: string) =>
      save.mutate(navIds.includes(id) ? navIds.filter((n) => n !== id) : [...navIds, id]),
  };
}

/** Navigable nodes by id, for resolving favorites and recent pages against the user's current menu. */
export function nodesById(items: NavNode[]): Map<string, NavNode> {
  return new Map(flattenNavigation(items).map(({ node }) => [node.id, node]));
}
